// SPDX-License-Identifier: AGPL-3.0-or-later

import Channels from '@app/features/channel/state/Channels';
import GuildMembers from '@app/features/member/state/GuildMembers';
import type {Message} from '@app/features/messaging/models/MessagingMessage';
import {
	hasExplicitMention,
	readMentionShape,
	resolveMentionSignals,
} from '@app/features/messaging/utils/MentionPredicate';
import {Logger} from '@app/features/platform/utils/AppLogger';
import type {User} from '@app/features/user/models/User';
import UserGuildSettings from '@app/features/user/state/UserGuildSettings';

const logger = new Logger('MessageUtils');

// `ignoreEveryone` is how a non-member of a thread is treated: @everyone in a thread they have
// not joined is not a mention of them, regardless of their guild suppression setting.
export function isMentioned(user: User, message: Message, ignoreEveryone = false): boolean {
	const channel = Channels.getChannel(message.channelId);
	if (channel == null) {
		logger.warn(`${message.channelId} does not exist!`);
		return false;
	}
	const guildId = channel.guildId ?? null;
	const guildMember = guildId != null ? GuildMembers.getMember(guildId, user.id) : null;
	return hasExplicitMention(
		resolveMentionSignals({
			...readMentionShape(message),
			currentUserId: user.id,
			authorId: message.author.id,
			memberRoleIds: guildMember?.roles ?? null,
			suppressEveryone: ignoreEveryone || UserGuildSettings.isEveryoneMentionSuppressed(guildId),
			suppressRoles: guildId == null || UserGuildSettings.isRoleMentionSuppressed(guildId),
		}),
	);
}
